import React from 'react'
import ReactDOM from 'react-dom'
import VectorLayer from 'ol/layer/Vector'
import Vector from 'ol/source/Vector'
import { transformExtent } from 'ol/proj'
import { ZoomToExtent, Zoom, Attribution, ScaleLine } from 'ol/control'
import { DragBox, Select, Draw } from 'ol/interaction'
import Polygon from 'ol/geom/Polygon'
import { FaSearchPlus, FaSearchMinus, FaGlobe } from 'react-icons/fa'
import { IconTooltip } from '../../../elem/Tooltip'
import { drawStyles } from './mapStyles'
import { Overlay } from 'ol'
import { getArea, getLength } from 'ol/sphere.js'
import LineString from 'ol/geom/LineString'
import { unByKey } from 'ol/Observable'
import ConvertMeasureLength, { convertMeasureArea } from './ConvertMeasureLength'

const createLabel = (element, name, tooltip) => {
    const div = document.createElement('div')
    div.setAttribute('class', 'zoomToExtentDiv')
    if (name && tooltip) {
        div.setAttribute('data-for', name)
        div.setAttribute('data-tip', tooltip)
    }
    ReactDOM.render(element, div)
    return div
}

const getLayerTypes = () => {
    return {
        polygon: new VectorLayer({
            source: new Vector(),
            title: 'Polygon',
            visible: false,
        }),
        measure: new VectorLayer({
            source: new Vector(),
            title: 'MeasureLines',
            visible: true,
            style: drawStyles.measureStyles
        }),
    }
}

const getControls = layers => {
    const select = getSelectControl()
    const boundingBox = getBoundingBoxControl()
    const polygon = getPolygonControl(layers.polygon)
    const measure = getMeasureControl(layers.measure, 'LineString')
    const measureArea = getMeasureControl(layers.measure, 'Polygon')
    return {
        select,
        boundingBox,
        polygon,
        measure,
        measureArea,
    }
}

const zoomControls = () => {
    return [
        new Zoom({
            className: 'ol-zoom zoomWrapper',
            zoomInLabel: createLabel(
                <>
                    <FaSearchPlus />
                    <IconTooltip id={'zoom-in'} />
                </>,
                'zoom-in',
                'Zoom In'
            ),
            zoomInTipLabel: '',
            zoomOutLabel: createLabel(
                <>
                    <FaSearchMinus />
                    <IconTooltip id={'zoom-out'} />
                </>,
                'zoom-out',
                'Zoom Out'
            ),
            zoomOutTipLabel: '',
        }),
        new ZoomToExtent({
            extent: transformExtent([-104.3, 39.88, -95.3091, 43.1], 'EPSG:4326', 'EPSG:3857'),
            label: createLabel(
                <>
                    <FaGlobe />
                    <IconTooltip id={'zoom-to-extent'} />
                </>,
                'zoom-to-extent',
                'Zoom to Full Extent'
            ),
            tipLabel: '',
            className: 'ol-zoom-extent zoomToExtentWrapper',
        }),
        new Attribution({
            className: 'ol-attribution attributionWrapper',
        }),
        new ScaleLine({
            units: 'imperial',
        })
    ]
}

const getSelectControl = () => {
    const control = new Select({
        filter: f => {
            // only allow points to be selected
            return f.getGeometry().getType() === 'Point'
        },
        style: e => {
            if (e.get('selected')) {
                return drawStyles.pointerStylesHighlighted
            } else {
                return drawStyles.pointerStylesSelected
            }
        }
    })

    const on = (source, updateSelectedFeatures) => {
        const mouseDownKey = control.on('select', function(e) {
            var tolerance = 10
            if (e.selected.length) {
                const map = e.mapBrowserEvent.map
                const coordinates = e.selected[0].getGeometry().flatCoordinates
                const targetPixel = map.getPixelFromCoordinate(coordinates)

                const pixel1 = [
                    targetPixel[0] - tolerance / 2,
                    targetPixel[1] - tolerance / 2,
                ]
                const pixel2 = [
                    targetPixel[0] - tolerance / 2,
                    targetPixel[1] + tolerance / 2,
                ]
                const pixel3 = [
                    targetPixel[0] + tolerance / 2,
                    targetPixel[1] + tolerance / 2,
                ]
                const pixel4 = [
                    targetPixel[0] + tolerance / 2,
                    targetPixel[1] - tolerance / 2,
                ]

                const point1 = map.getCoordinateFromPixel(pixel1)
                const point2 = map.getCoordinateFromPixel(pixel2)
                const point3 = map.getCoordinateFromPixel(pixel3)
                const point4 = map.getCoordinateFromPixel(pixel4)

                const polygon = new Polygon([[point1, point2, point3, point4]])
                
                const newFilterData = source.getFeatures() 
                    .filter(feature => {
                        const isSelected = feature.get('selected')
                        const isDisplayed = feature.get('displayed')
                        if (isSelected && e.mapBrowserEvent.originalEvent.altKey) {
                            return isDisplayed && feature
                        } else {
                            return isDisplayed && polygon.intersectsExtent(
                                feature.getGeometry().getExtent()
                            )
                        }
                    })

                updateSelectedFeatures(newFilterData)
            }
        })
        const keys = [mouseDownKey]
        return keys
    }

    return {
        active: false,
        control,
        on,
    }
}

const getBoundingBoxControl = () => {
    const control = new DragBox()

    const on = (source, updateSelectedFeatures) => {
        const boxEndKey = control.on('boxend', e => {
            const extent = control.getGeometry().getExtent()
            let newFilterData = source.getFeaturesInExtent(extent).filter(x => x.get('displayed'))
            if (e.mapBrowserEvent.originalEvent.altKey) {
                const alreadySelectedFeatures = source.getFeatures().filter(x => x.get('displayed') && x.get('selected'))
                newFilterData = newFilterData.concat(alreadySelectedFeatures)                
            }
            updateSelectedFeatures(newFilterData)
        })
        const keys = [boxEndKey]
        return keys
    }

    return {
        active: false,
        control,
        on,
    }
}

const getPolygonControl = layer => {
    let altKeyPressed = false
    const control = new Draw({
        type: 'Polygon',
        source: layer.getSource(),
        layer,
        style: drawStyles.polygonStyles,
        condition: e => {
            altKeyPressed = e.originalEvent.altKey            
            return true
        }
    })
    const on = (source, updateSelectedFeatures) => {
        const drawEndKey = control.on('drawend', e => {
            const polygon = e.feature.getGeometry()
            let newFilterData = source.getFeatures()
                .filter(feature =>
                    feature.get('displayed') && polygon.intersectsExtent(feature.getGeometry().getExtent())
                )
            if (altKeyPressed) {
                const alreadySelectedFeatures = source.getFeatures().filter(x => x.get('displayed') && x.get('selected'))
                newFilterData = newFilterData.concat(alreadySelectedFeatures)                
            }
            updateSelectedFeatures(newFilterData)
        })
        const keys = [drawEndKey]
        return keys
    }
    return {
        active: false,
        control,
        layer,
        on,
    }
}

const createMeasureTooltip = (element, map) => {
    if (element) {
        element.remove()
    }
    let tooltip = new Overlay({
        element: element,
        offset: [0, -15],
        positioning: 'bottom-center',
        stopEvent: false,
        insertFirst: false,
    })
    map.addOverlay(tooltip)
    return tooltip
}

const getMeasureControl = (layer, type) => {
    const control = new Draw({
        source: layer.getSource(),
        type: type,
        style: (feature) => {
            const geometryType = feature.getGeometry().getType()
            if (geometryType === type || geometryType === 'Point') {
                return drawStyles.measureStyles
            }
        },
    })

    const on = (source, updateSelectedFeatures, options) => {
        const { measureUnit } = options
        const formatLength = (line) => {
            const length = getLength(line)
            let finalValue = ConvertMeasureLength(length, measureUnit ? measureUnit : 'us')                        
            return finalValue
        }
        const formatArea = (polygon) => {
        const area = getArea(polygon)
          let finalValue = convertMeasureArea(area, measureUnit ? measureUnit : 'us')                        
          return finalValue
        }

        let sketch
        let listener
        let measureTooltipElement = document.createElement('div')
        let drawStart = control.on('drawstart', (evt) => {
            // set sketch
            sketch = evt.feature
            const evtMap = evt.target.getMap()
            measureTooltipElement = document.createElement('div')
            measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure'
            let measureTooltip = createMeasureTooltip(measureTooltipElement, evtMap)

            listener = sketch.getGeometry().on('change', (e) => {
                const geom = e.target
                let output, tooltipCoord
                if (geom instanceof LineString) {
                    output = formatLength(geom)
                    tooltipCoord = geom.getLastCoordinate()
                } else if (geom instanceof Polygon) {
                    output = formatArea(geom)
                    tooltipCoord = geom.getInteriorPoint().getCoordinates();
                }
                measureTooltipElement.innerHTML = output
                measureTooltip.setPosition(tooltipCoord)
            })
        })
        
        let drawEnd = control.on('drawend', (evt) => {
            measureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
            evt.feature.setStyle(() => drawStyles.measureStrokeStyles)           

            // unset sketch
            sketch = null
            // unset tooltip so that a new one can be created
            // let measureTooltipElement = document.createElement('div')
            measureTooltipElement = null
            const evtMap = evt.target.getMap()
            createMeasureTooltip(measureTooltipElement, evtMap)
            unByKey(listener)
        })

        const keys = [drawStart, drawEnd]
        return keys
    }

    return {
        active: false,
        control,
        layer,
        on,
        addToMap: 1
    }
}

const getDisplayStyleProps = (e, pixel) => {
    // horizontal edge detection
    const tooltipWidth = 400
    // const windowWidth = e.pointerEvent.view.innerWidth //browser's windows current size
    const clientX = e.originalEvent.clientX //position of the pointer 
    const mapWidth = e.originalEvent.currentTarget.scrollWidth
    const tooltipRightPxValue = clientX + tooltipWidth
    const RightEdgeOfTheMapXCoord = mapWidth + (mapWidth - clientX)
    const exceedsRightEdge = tooltipRightPxValue > RightEdgeOfTheMapXCoord
    const horizontalProps = exceedsRightEdge
        ? { right: mapWidth - pixel[0] + 'px' }
        : { left: pixel[0] + 'px' }

    // vertical edge detection
    const tooltipHeight = 270
    const tooltipYTopOffset = 100
    const tooltipYBottomOffset = tooltipHeight - tooltipYTopOffset
    const pixelY = pixel[1]
    
    // top edge
    const topDelta = pixelY - tooltipYTopOffset
    const exceedsTopEdge = topDelta < 0

    // bottom edge
    const bottomPixel = e.originalEvent.currentTarget.clientHeight
    const bottomDelta = pixelY - 10 + tooltipYBottomOffset - bottomPixel
    const exceedsBottomEdge = bottomDelta > 0
    
    const verticalProps = exceedsTopEdge
        ? { top: pixel[1] - (tooltipYTopOffset + topDelta) + 10 + 'px' }
        : exceedsBottomEdge ? 
            { top: pixel[1] - tooltipYTopOffset - bottomDelta } 
            : { top: pixel[1] - tooltipYTopOffset + 'px' }

    const styleProps = {
        ...horizontalProps,
        ...verticalProps,
    }
    return styleProps
}

const displayPopup = async (e, map, setTooltipState, fetchTooltipData) => {
    const { feature } = tieEventToPopup(e, map)
    if (feature) {
        const featurePixel = map.getPixelFromCoordinate(feature.getGeometry().getCoordinates())
        const styleProps = getDisplayStyleProps(e, featurePixel)
        // fetch the tooltip data if we have a new clearinghouse number
        const clearinghouseNumber = feature.get('ClearinghouseNumber')
        feature.set('highlighted', 1)
        if (map.get('currentClearinghouseNumber') !== clearinghouseNumber) {
            map.set('currentClearinghouseNumber', clearinghouseNumber)
            map.set('currentFeature', feature)
            fetchTooltipData(clearinghouseNumber)
        }
        setTooltipState({
            ...styleProps,
            clearinghouseNumber,
        })
    } else {
        // if we're not over a feature,
        // set clearinghouse number to null
        setTooltipState(prevState => ({
            ...prevState,
            clearinghouseNumber: null,
        }))
    }
}

const tieEventToPopup = (e, map) => {
    const pixel = map.getEventPixel(e.originalEvent)
    // const styleProps = getDisplayStyleProps(e, pixel)

    // unhighlight the previous feature
    const previousFeature = map.get('currentFeature')
    if (previousFeature) {
        previousFeature.set('highlighted', 0)
    }

    let allFeaturesInPixel = []
    map.forEachFeatureAtPixel(pixel, (f, layer) => {
        allFeaturesInPixel.push(f)
    })
    if (allFeaturesInPixel.length > 1) {
        //NW-642          
        allFeaturesInPixel.sort((a, b) => {
            // First sort by selected
            if (a.get('selected') !== b.get('selected')) {
                return b.get('selected') - a.get('selected')
            }
            // Then sort by ClearinghouseNumber (ascending)
            return a.get('ClearinghouseNumber') - b.get('ClearinghouseNumber')
        })
        // below solution probably also works for this
        // const selectedFeaturesInPixel = allFeaturesInPixel.filter(x => x.get('selected')) // get selected features
        // allFeaturesInPixel = selectedFeaturesInPixel && selectedFeaturesInPixel.length ? selectedFeaturesInPixel.filter((f, i, arr) => f.get('ClearinghouseNumber') === Math.min(...arr.map(x => x.get('ClearinghouseNumber')))) : allFeaturesInPixel // get the smallest ClearinghouseNumber that's selected
    }
    return { feature: allFeaturesInPixel[0] }
}
export { getControls, getLayerTypes, zoomControls, displayPopup }
